import { OrbVisualizer } from '@qi/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Activity,
  Camera,
  Droplets,
  Flame,
  Heart,
  Mic,
  Moon,
  Scale,
  Sparkles,
  Sun,
  Waves,
  Wind,
} from 'lucide-react';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

export const Route = createFileRoute('/_marketing/science')({
  component: SciencePage,
});

const STEPS = [
  {
    icon: Camera,
    label: 'Capture',
    body: 'Your webcam and microphone read subtle signals — facial steadiness, blink and breathing rhythm, vocal tone. A single tongue-and-face photo adds a traditional colour read. Everything is processed in your browser.',
  },
  {
    icon: Waves,
    label: 'Resonate',
    body: 'A short sweep of frequencies plays while we watch how your body responds. The frequencies your system reacts to most become your personal resonance peaks.',
  },
  {
    icon: Sparkles,
    label: 'Personalize',
    body: 'Signals and reads combine into a set of plain-language scores and a tailored protocol — the frequencies and intentions most likely to move you toward your goal.',
  },
] as const;

const SIGNALS = [
  {
    icon: Camera,
    source: 'Face camera',
    items: [
      'Blink rate',
      'Facial stability',
      'Breathing rhythm',
      'Jaw & forehead tension',
      'Pupil response',
    ],
  },
  {
    icon: Mic,
    source: 'Voice',
    items: ['Pitch', 'Stability', 'Strain / stress', 'Breathiness', 'Jitter & shimmer'],
  },
  {
    icon: Sun,
    source: 'Tongue & face photo',
    items: ['Body & coating colour', 'Moisture', 'Lustre', 'Per-zone reads'],
  },
] as const;

const WELLNESS_METRICS = [
  {
    icon: Activity,
    label: 'Focus',
    sub: 'Autonomic regulation',
    body: 'How steady and settled your attention reads — drawn from facial steadiness and how calm your voice sounds.',
  },
  {
    icon: Sun,
    label: 'Energy',
    sub: 'Cardiovascular drive',
    body: 'Your available drive and activation — read from your resonance response and the steadiness of your voice.',
  },
  {
    icon: Moon,
    label: 'Recovery',
    sub: 'Regenerative capacity',
    body: 'How well your system rests and repairs — read from how relaxed your voice sounds and your resonance response.',
  },
  {
    icon: Heart,
    label: 'Mood',
    sub: 'Limbic balance',
    body: 'Emotional evenness and stability — read from facial steadiness and an even, stable voice.',
  },
  {
    icon: Wind,
    label: 'Calm',
    sub: 'Stress-axis load',
    body: 'How relaxed vs. strained you read — drawn from the tension and strain picked up in your voice.',
  },
  {
    icon: Sparkles,
    label: 'Resonance',
    sub: 'Overall coherence',
    body: 'A single composite of the reads above — your overall sense of balance and how strongly your system responded.',
  },
] as const;

const TCM_METRICS = [
  {
    icon: Scale,
    label: 'Balance',
    body: 'Yin / yang harmony. 100 sits right in the middle — neither overheated nor too cool, neither dry nor damp.',
  },
  {
    icon: Sparkles,
    label: 'Vitality',
    body: 'Overall vigour, read from complexion lustre and tongue health. Radiant skin and a healthy pink tongue score high.',
  },
  {
    icon: Flame,
    label: 'Heat',
    body: 'Thermal balance. 100 is comfortably warm; redness, yellow coating and dryness — or marked pallor — pull it down.',
  },
  {
    icon: Droplets,
    label: 'Fluids',
    body: 'Hydration. 100 means tongue and skin read well-moistened; dryness, cracks or flaky skin lower it.',
  },
  {
    icon: Activity,
    label: 'Circulation',
    body: 'Blood & qi flow. 100 means colour and vitality move freely; dusky tongue or sallow patches lower it.',
  },
  {
    icon: Heart,
    label: 'Organ harmony',
    body: 'A whole-system read. 100 means the facial zones and tongue map read evenly across body systems.',
  },
] as const;

// Tongue zones → traditional body-system map. Mirrors apps/api/src/routes/tongue.ts.
const TONGUE_ZONES = [
  { region: 'Tip', system: 'Heart / circulation', color: '#f87171' },
  { region: 'Mid', system: 'Lung / respiratory', color: '#a78bfa' },
  { region: 'Centre', system: 'Spleen / stomach', color: '#fbbf24' },
  { region: 'Sides', system: 'Liver / gallbladder', color: '#14b8a6' },
  { region: 'Root', system: 'Kidney / bladder', color: '#60a5fa' },
] as const;

// Face zones → traditional body-system map. Mirrors apps/api/src/routes/tongue.ts.
const FACE_ZONES = [
  { region: 'Forehead', system: 'Heart / circulation', color: '#f87171' },
  { region: 'Between the brows', system: 'Liver', color: '#14b8a6' },
  { region: 'Under-eyes', system: 'Kidney / fluids', color: '#60a5fa' },
  { region: 'Nose bridge', system: 'Spleen / digestion', color: '#fbbf24' },
  { region: 'Cheeks', system: 'Lung / respiratory', color: '#a78bfa' },
  { region: 'Chin / jaw', system: 'Kidney / hormonal', color: '#818cf8' },
] as const;

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
};

function SciencePage() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <div className="mb-14 flex flex-col items-center gap-4 text-center">
        <OrbVisualizer size={72} rings={3} />
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">The science</p>
        <h1 className="font-display text-5xl leading-[1.05] text-ink-0">How the scan reads you</h1>
        <p className="max-w-xl text-ink-1">
          A quick, camera-and-voice read paired with a traditional tongue-and-face map. Here is
          every signal we capture, every score we show, and what each one means.
        </p>
      </div>

      <div className="space-y-20 text-ink-1">
        {/* How it works */}
        <Section eyebrow="The flow" title="Three steps, about two minutes">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.label}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="qi-glass space-y-3 rounded-lg p-6"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-aurora/30 bg-surface-2 text-aurora">
                    <step.icon size={18} strokeWidth={1.7} />
                  </span>
                  <h3 className="font-display text-xl text-ink-0">{step.label}</h3>
                </div>
                <p className="text-sm leading-relaxed text-ink-1">{step.body}</p>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* Signals captured */}
        <Section eyebrow="Inputs" title="What we actually measure">
          <p className="mb-6 max-w-2xl text-sm text-ink-2">
            None of this leaves your device as raw data — frames and audio are read in the browser
            and only aggregate scores are kept.
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {SIGNALS.map((sig) => (
              <motion.div
                key={sig.source}
                {...fadeUp}
                className="overflow-hidden rounded-lg border border-hairline bg-surface-1"
              >
                <div className="flex items-center gap-2 border-b border-hairline bg-surface-2 px-5 py-3">
                  <sig.icon size={16} strokeWidth={1.7} className="text-jade" />
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink-1">
                    {sig.source}
                  </span>
                </div>
                <ul className="space-y-1.5 px-5 py-4 text-sm text-ink-1">
                  {sig.items.map((it) => (
                    <li key={it} className="flex items-center gap-2">
                      <span className="h-1 w-1 rounded-full bg-jade" />
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* Wellness metrics */}
        <Section eyebrow="Live scores" title="Your wellness metrics">
          <p className="mb-6 max-w-2xl text-sm text-ink-2">
            Read from your face and voice in real time. Every score runs 0–100 — higher is more
            balanced.
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {WELLNESS_METRICS.map((m) => (
              <MetricCard key={m.label} icon={m.icon} label={m.label} sub={m.sub} body={m.body} />
            ))}
          </div>
        </Section>

        {/* Tongue map */}
        <Section eyebrow="舌診 · Tongue reading" title="The tongue map">
          <p className="mb-8 max-w-2xl text-sm text-ink-2">
            In traditional Chinese medicine the tongue is read like a map — each zone is associated
            with a different body system. We read the colour, coating, and moisture of each zone and
            translate it into a plain-language note.
          </p>
          <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2">
            <TongueDiagram />
            <ZoneLegend zones={TONGUE_ZONES} />
          </div>
        </Section>

        {/* Face map */}
        <Section eyebrow="面色 · Complexion reading" title="The face map">
          <p className="mb-8 max-w-2xl text-sm text-ink-2">
            The face is read the same way. Colour, lustre, and shadowing across distinct areas each
            carry a traditional association with a body system.
          </p>
          <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2">
            <FaceDiagram />
            <ZoneLegend zones={FACE_ZONES} />
          </div>
        </Section>

        {/* TCM scores */}
        <Section eyebrow="Photo read" title="Tongue & face scores">
          <p className="mb-6 max-w-2xl text-sm text-ink-2">
            The zone reads roll up into six whole-body scores. These appear when you add a
            tongue-and-face photo to your scan.
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {TCM_METRICS.map((m) => (
              <MetricCard key={m.label} icon={m.icon} label={m.label} body={m.body} />
            ))}
          </div>
        </Section>

        {/* Disclaimer */}
        <section className="qi-glass space-y-2 rounded-lg p-6">
          <h2 className="font-display text-xl text-ink-0">Not a medical device</h2>
          <p className="text-sm text-ink-2">
            Qi Scan supports wellness exploration. The tongue and face reads are tradition-based
            pattern associations, not a diagnosis. Nothing here diagnoses, cures, or replaces
            medical professionals. If something feels wrong, see a clinician.
          </p>
        </section>

        <div className="flex flex-col items-center gap-4 pt-4 text-center">
          <p className="font-display text-2xl text-ink-0">See your own read.</p>
          <Link
            to="/onboarding"
            className="rounded-pill border border-aurora/40 bg-surface-2 px-7 py-3 font-mono text-xs uppercase tracking-[0.18em] text-ink-0 transition-colors hover:bg-surface-3"
          >
            Begin your scan →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <motion.section {...fadeUp} className="space-y-4">
      <div className="space-y-1">
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-aurora">{eyebrow}</p>
        <h2 className="font-display text-3xl text-ink-0">{title}</h2>
      </div>
      {children}
    </motion.section>
  );
}

function MetricCard({
  icon: Icon,
  label,
  sub,
  body,
}: {
  icon: typeof Activity;
  label: string;
  sub?: string;
  body: string;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-hairline bg-surface-1 p-5">
      <div className="flex items-center gap-2">
        <Icon size={17} strokeWidth={1.7} className="text-aurora" />
        <h3 className="font-display text-lg text-ink-0">{label}</h3>
        {sub ? (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
            {sub}
          </span>
        ) : null}
      </div>
      <p className="text-sm leading-relaxed text-ink-1">{body}</p>
    </div>
  );
}

function ZoneLegend({
  zones,
}: {
  zones: readonly { region: string; system: string; color: string }[];
}) {
  return (
    <ul className="space-y-2.5">
      {zones.map((z) => (
        <li
          key={z.region}
          className="flex items-center gap-3 rounded-md border border-hairline bg-surface-1 px-4 py-2.5"
        >
          <span
            className="h-3 w-3 shrink-0 rounded-full"
            style={{ background: z.color, boxShadow: `0 0 8px ${z.color}` }}
          />
          <span className="font-mono text-xs uppercase tracking-[0.14em] text-ink-0">
            {z.region}
          </span>
          <span className="ml-auto text-sm text-ink-2">{z.system}</span>
        </li>
      ))}
    </ul>
  );
}

/** Stylized tongue with the five TCM zones coloured to match the legend. */
function TongueDiagram() {
  return (
    <svg
      viewBox="0 0 220 280"
      className="mx-auto w-full max-w-[260px]"
      role="img"
      aria-label="Tongue zone map: tip is heart, mid is lung, centre is spleen, sides are liver, root is kidney"
    >
      <defs>
        <clipPath id="tongue-shape">
          <path d="M110 12 C150 12 186 36 186 96 C186 150 172 214 142 254 C130 270 110 274 110 274 C110 274 90 270 78 254 C48 214 34 150 34 96 C34 36 70 12 110 12 Z" />
        </clipPath>
      </defs>

      {/* Zone fills, clipped to the tongue silhouette */}
      <g clipPath="url(#tongue-shape)">
        {/* Root — Kidney */}
        <rect x="0" y="0" width="220" height="66" fill="#60a5fa" opacity="0.85" />
        {/* Mid — Lung */}
        <rect x="0" y="66" width="220" height="44" fill="#a78bfa" opacity="0.85" />
        {/* Centre — Spleen */}
        <rect x="0" y="110" width="220" height="78" fill="#fbbf24" opacity="0.85" />
        {/* Tip — Heart */}
        <rect x="0" y="188" width="220" height="92" fill="#f87171" opacity="0.85" />
        {/* Sides — Liver (overlay on left & right edges across the body) */}
        <rect x="0" y="66" width="38" height="160" fill="#14b8a6" opacity="0.92" />
        <rect x="182" y="66" width="38" height="160" fill="#14b8a6" opacity="0.92" />
      </g>

      {/* Outline */}
      <path
        d="M110 12 C150 12 186 36 186 96 C186 150 172 214 142 254 C130 270 110 274 110 274 C110 274 90 270 78 254 C48 214 34 150 34 96 C34 36 70 12 110 12 Z"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.55"
        strokeWidth="2"
      />
      {/* Centre crease */}
      <path
        d="M110 30 C112 110 112 190 110 262"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.3"
        strokeWidth="1.5"
        strokeDasharray="4 5"
      />

      {/* Labels */}
      <g
        fill="var(--ink-0)"
        fontFamily="var(--font-mono)"
        fontSize="11"
        textAnchor="middle"
        style={{ textTransform: 'uppercase', letterSpacing: '0.12em' }}
      >
        <text x="110" y="40">
          Root
        </text>
        <text x="110" y="92">
          Mid
        </text>
        <text x="110" y="150">
          Centre
        </text>
        <text x="110" y="232">
          Tip
        </text>
      </g>
      <text
        x="110"
        y="200"
        fill="var(--ink-0)"
        fontFamily="var(--font-mono)"
        fontSize="11"
        textAnchor="middle"
        style={{ letterSpacing: '0.12em' }}
      >
        SIDES
      </text>
    </svg>
  );
}

/** Stylized face with the six TCM reading zones coloured to match the legend. */
function FaceDiagram() {
  const faceOutline =
    'M110 32 C154 32 186 60 186 112 C186 156 170 200 140 224 C129 233 120 236 110 236 C100 236 91 233 80 224 C50 200 34 156 34 112 C34 60 66 32 110 32 Z';
  return (
    <svg
      viewBox="0 0 220 280"
      className="mx-auto w-full max-w-[260px]"
      role="img"
      aria-label="Face zone map: forehead is heart, between brows is liver, under-eyes is kidney, nose bridge is spleen, cheeks are lung, chin is kidney hormonal"
    >
      <defs>
        <clipPath id="face-shape">
          <path d={faceOutline} />
        </clipPath>
      </defs>

      <g clipPath="url(#face-shape)">
        {/* Base keeps the translucent zones reading cleanly in both themes */}
        <rect x="0" y="0" width="220" height="280" fill="var(--surface-1)" />
        {/* Forehead — Heart */}
        <rect x="0" y="0" width="220" height="98" fill="#f87171" opacity="0.8" />
        {/* Cheeks band — Lung */}
        <rect x="0" y="98" width="220" height="80" fill="#a78bfa" opacity="0.8" />
        {/* Chin / jaw — Kidney hormonal */}
        <rect x="0" y="178" width="220" height="102" fill="#818cf8" opacity="0.8" />
        {/* Nose bridge — Spleen (central vertical strip) */}
        <rect x="101" y="98" width="18" height="52" rx="8" fill="#fbbf24" opacity="0.92" />
        {/* Between brows — Liver */}
        <rect x="101" y="82" width="18" height="15" rx="4" fill="#14b8a6" opacity="0.95" />
      </g>

      {/* Under-eye markers — Kidney / fluids */}
      <ellipse cx="80" cy="118" rx="14" ry="6" fill="#60a5fa" opacity="0.95" />
      <ellipse cx="140" cy="118" rx="14" ry="6" fill="#60a5fa" opacity="0.95" />

      {/* Face outline */}
      <path
        d={faceOutline}
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.55"
        strokeWidth="2"
      />

      {/* Eyebrows — arched */}
      <path
        d="M65 94 Q80 85 95 93"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.5"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M125 93 Q140 85 155 94"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.5"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Eyes — almond with lash flick */}
      <path
        d="M68 106 Q80 99 92 106 Q80 112 68 106 Z"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.55"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M128 106 Q140 99 152 106 Q140 112 128 106 Z"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.55"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="80" cy="106" r="2.4" fill="var(--ink-0)" opacity="0.6" />
      <circle cx="140" cy="106" r="2.4" fill="var(--ink-0)" opacity="0.6" />
      <path
        d="M92 105 l5 -3"
        stroke="var(--ink-0)"
        strokeOpacity="0.45"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="M128 105 l-5 -3"
        stroke="var(--ink-0)"
        strokeOpacity="0.45"
        strokeWidth="1.3"
        strokeLinecap="round"
      />

      {/* Nose */}
      <path
        d="M110 110 L106 142 Q110 147 114 142"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.4"
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      {/* Lips — fuller, soft tint */}
      <path
        d="M95 176 Q103 170 110 173 Q117 170 125 176 Q117 187 110 187 Q103 187 95 176 Z"
        fill="#f87171"
        opacity="0.4"
      />
      <path
        d="M95 176 Q110 180 125 176"
        fill="none"
        stroke="var(--ink-0)"
        strokeOpacity="0.45"
        strokeWidth="1.3"
        strokeLinecap="round"
      />

      {/* Earrings */}
      <circle cx="48" cy="186" r="2.6" fill="#fbbf24" opacity="0.9" />
      <circle cx="172" cy="186" r="2.6" fill="#fbbf24" opacity="0.9" />

      {/* Labels */}
      <g
        fill="var(--ink-0)"
        fontFamily="var(--font-mono)"
        fontSize="10"
        textAnchor="middle"
        style={{ letterSpacing: '0.1em' }}
      >
        <text x="110" y="58">
          FOREHEAD
        </text>
        <text x="110" y="224">
          CHIN / JAW
        </text>
      </g>
    </svg>
  );
}
